import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Swords, Settings, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { useLitLegionTasks } from '../hooks/useLitLegionTasks';
import { useLitLegionStats } from '../hooks/useLitLegionStats';
import { TaskCard } from '../components/TaskCard';
import { LitLegionStatsCard } from '../components/LitLegionStatsCard';
import { ArcheHeader } from '../../../components/ArcheHeader';

export function TaskListPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [category, setCategory] = useState('all');
  // Server route gate: `requireAdminOrLitcraftAdmin` (docs/api-admin.md).
  const isAdmin = useArcheAdmin('litcraft_admin');
  const navigate = useNavigate();

  const statusFilter =
    activeTab === 'all' ? undefined : activeTab === 'in-progress' ? 'accepted' : 'completed';
  const categoryFilter = category === 'all' ? undefined : category;

  const { tasks, loading, error, hasMore, loadMore, refresh } = useLitLegionTasks({
    status: statusFilter,
    category: categoryFilter,
  });
  const { stats, loading: statsLoading } = useLitLegionStats();

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const t of tasks) {
      if (t.task?.category) cats.add(t.task.category);
    }
    return Array.from(cats).sort();
  }, [tasks]);

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader
        archeId="arche_lit_legion"
        title="Lit Legion"
        leftActions={<Swords className="h-6 w-6" />}
      >
        <Button variant="ghost" size="icon" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </ArcheHeader>
      <div className="max-w-6xl mx-auto p-6 space-y-6 w-full">
        <p className="text-sm text-muted-foreground">Complete quests, earn rewards, and level up</p>

        <LitLegionStatsCard stats={stats} loading={statsLoading} />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <TabsList>
              <TabsTrigger value="all">All Tasks</TabsTrigger>
              <TabsTrigger value="in-progress">In Progress</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
            </TabsList>

            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && <p className="text-sm text-destructive mt-2">{error}</p>}

          <TabsContent value={activeTab} className="space-y-4 mt-4">
            {loading && tasks.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-44 rounded-lg" />
                ))}
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-12">
                <Swords className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {activeTab === 'completed'
                    ? 'No completed tasks yet. Get started on a quest!'
                    : activeTab === 'in-progress'
                      ? 'No tasks in progress. Accept a quest to begin!'
                      : 'No tasks available right now. Check back later!'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tasks.map((task) => (
                    <TaskCard key={task.task.taskId} task={task} />
                  ))}
                </div>
                {hasMore && (
                  <div className="text-center">
                    <Button variant="ghost" onClick={loadMore} disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load more'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {isAdmin && (
          <Card
            className="hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => navigate('/a/arche_lit_legion/admin')}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <Settings className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Admin</p>
                  <p className="text-sm text-muted-foreground">
                    Manage tasks, NPCs, and player progression
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
